import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, X, RotateCcw, Plus } from "lucide-react";

const PIPELINE_REACTIVATE_TARGETS = [
  "Sourcing & Acquisition",
  "Cleaning & Repairing",
  "Ironing",
  "Weighing",
  "Photographing",
  "Studio Editing",
  "Listing & Marketing",
  "Sale Completion",
  "Packaging",
  "Shipping",
  "Confirmation of Receipt",
];

/**
 * DangerZone — bottom-of-page red-bordered section on AdminInventoryEdit that lets the
 * admin discontinue a piece (damaged / lost / returned / discarded / donated / custom) or
 * reactivate a previously-discontinued piece back into the live pipeline.
 *
 * Props:
 *  - item: the inventory item
 *  - onChanged(updated): called with the server-acknowledged item after success
 */
export default function DangerZone({ item, onChanged }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const isDiscontinued = item.stage === "Discontinued";

  return (
    <section className="my-12 border-2 border-red-700/40 bg-red-50/30 p-6" data-testid="danger-zone">
      <div className="flex items-start justify-between">
        <div>
          <p className="editorial-label text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> danger zone
          </p>
          <h3 className="font-serif text-2xl lowercase text-charcoal mt-2">
            {isDiscontinued ? "this piece is archived." : "discontinue this piece."}
          </h3>
          {isDiscontinued ? (
            <div className="mt-2 text-xs text-espresso">
              <p>reason · <span className="font-medium">{(item.discontinue_reason || "—").replace(/_/g, " ")}</span></p>
              {item.discontinue_notes && <p className="mt-1">notes · {item.discontinue_notes}</p>}
              {item.carrier_claim_ref && <p className="mt-1">carrier claim · {item.carrier_claim_ref}</p>}
              {item.discontinued_at && <p className="mt-1 text-taupe">discontinued · {item.discontinued_at.slice(0,10)} by {item.discontinued_by || "—"}</p>}
            </div>
          ) : (
            <p className="text-xs text-espresso mt-2 max-w-md">
              use this when a piece is damaged, lost in transit, returned-unsalvageable, discarded, donated, or otherwise won&apos;t be sold. it&apos;s reversible — the audit log keeps the paper trail either way.
            </p>
          )}
        </div>
        {isDiscontinued ? (
          <button
            onClick={() => setReactivateOpen(true)}
            data-testid="reactivate-btn"
            className="inline-flex items-center gap-2 border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-xs tracking-[0.25em] uppercase"
          >
            <RotateCcw className="w-3 h-3" /> reactivate
          </button>
        ) : (
          <button
            onClick={() => setDialogOpen(true)}
            data-testid="discontinue-btn"
            className="inline-flex items-center gap-2 border border-red-700 text-red-700 hover:bg-red-700 hover:text-ivory px-4 py-2 text-xs tracking-[0.25em] uppercase"
          >
            <AlertTriangle className="w-3 h-3" /> discontinue
          </button>
        )}
      </div>

      {dialogOpen && (
        <DiscontinueDialog itemId={item.id} onClose={() => setDialogOpen(false)} onSaved={(d) => { setDialogOpen(false); onChanged(d); }} />
      )}
      {reactivateOpen && (
        <ReactivateDialog itemId={item.id} onClose={() => setReactivateOpen(false)} onSaved={(d) => { setReactivateOpen(false); onChanged(d); }} />
      )}
    </section>
  );
}

function DiscontinueDialog({ itemId, onClose, onSaved }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [claim, setClaim] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newReason, setNewReason] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/discontinue_reasons");
      setReasons(data || []);
      if (data?.length && !reason) setReason(data[0].slug);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const addReason = async () => {
    const name = newReason.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/discontinue_reasons", { name });
      toast.success(`reason "${data.name}" added.`);
      setNewReason("");
      setShowAdd(false);
      await load();
      setReason(data.slug);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not add reason.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) { toast.error("pick a reason."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/inventory/${itemId}/discontinue`, { reason, notes, carrier_claim_ref: claim });
      toast.success("piece discontinued.");
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not discontinue.");
    } finally {
      setSubmitting(false);
    }
  };

  const needsCarrier = /lost|transit|carrier/i.test(reason);

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-auto" onClick={onClose} data-testid="discontinue-dialog">
      <form onSubmit={submit} className="bg-ivory border border-taupe max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="close" className="absolute top-4 right-4 text-taupe hover:text-charcoal" data-testid="discontinue-close">
          <X className="w-5 h-5" />
        </button>
        <p className="editorial-label text-red-700 flex items-center gap-2"><AlertTriangle className="w-3 h-3" /> discontinue</p>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-1">remove from inventory.</h2>

        <label className="editorial-label block mt-6">reason</label>
        <div className="flex items-center gap-2 mt-1">
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="discontinue-reason" className="flex-1 bg-transparent border-b border-taupe py-2 text-charcoal focus:outline-none focus:border-brass">
            {reasons.map((r) => <option key={r.slug} value={r.slug}>{r.name.toLowerCase()}</option>)}
          </select>
          <button type="button" onClick={() => setShowAdd((v) => !v)} className="border border-taupe text-charcoal hover:border-brass hover:text-brass p-1.5" data-testid="discontinue-add-reason-toggle">
            <Plus className="w-3 h-3" />
          </button>
        </div>
        {showAdd && (
          <div className="flex items-center gap-2 mt-2" data-testid="discontinue-add-reason-row">
            <input type="text" value={newReason} onChange={(e) => setNewReason(e.target.value)} placeholder="custom reason name" data-testid="discontinue-new-reason" className="flex-1 bg-transparent border-b border-brass py-1 text-charcoal text-sm focus:outline-none" />
            <button type="button" onClick={addReason} className="text-[0.6rem] tracking-widest uppercase border border-brass text-brass hover:bg-brass hover:text-ivory px-3 py-1" data-testid="discontinue-add-reason-submit">
              add
            </button>
          </div>
        )}

        <label className="editorial-label block mt-6">notes</label>
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="what happened?" data-testid="discontinue-notes" className="w-full bg-transparent border-b border-taupe py-2 mt-1 text-charcoal text-sm focus:outline-none" />

        {needsCarrier && (
          <>
            <label className="editorial-label block mt-6">carrier claim ref (optional)</label>
            <input type="text" value={claim} onChange={(e) => setClaim(e.target.value)} placeholder="e.g. FX-9988-XYZ" data-testid="discontinue-claim" className="w-full bg-transparent border-b border-taupe py-2 mt-1 text-charcoal text-sm focus:outline-none" />
          </>
        )}

        <div className="mt-8 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs tracking-[0.25em] uppercase text-taupe hover:text-charcoal px-3 py-2" data-testid="discontinue-cancel">
            never mind
          </button>
          <button type="submit" disabled={submitting} data-testid="discontinue-submit" className="bg-red-700 hover:bg-red-800 text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase disabled:opacity-50">
            {submitting ? "discontinuing…" : "discontinue piece"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ReactivateDialog({ itemId, onClose, onSaved }) {
  const [target, setTarget] = useState("Sourcing & Acquisition");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/inventory/${itemId}/reactivate`, { target_stage: target, notes });
      toast.success("piece reactivated.");
      onSaved(data);
    } catch (e) {
      toast.error("could not reactivate.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-auto" onClick={onClose} data-testid="reactivate-dialog">
      <form onSubmit={submit} className="bg-ivory border border-taupe max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="close" className="absolute top-4 right-4 text-taupe hover:text-charcoal" data-testid="reactivate-close">
          <X className="w-5 h-5" />
        </button>
        <p className="editorial-label text-brass flex items-center gap-2"><RotateCcw className="w-3 h-3" /> reactivate</p>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-1">bring this piece back.</h2>
        <p className="text-xs text-espresso mt-2">choose which stage of the pipeline it should re-enter.</p>

        <label className="editorial-label block mt-6">target stage</label>
        <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="reactivate-target" className="w-full bg-transparent border-b border-taupe py-2 mt-1 text-charcoal focus:outline-none focus:border-brass">
          {PIPELINE_REACTIVATE_TARGETS.map((s) => <option key={s} value={s}>{s.toLowerCase()}</option>)}
        </select>

        <label className="editorial-label block mt-6">notes (optional)</label>
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="why is it coming back?" data-testid="reactivate-notes" className="w-full bg-transparent border-b border-taupe py-2 mt-1 text-charcoal text-sm focus:outline-none" />

        <div className="mt-8 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs tracking-[0.25em] uppercase text-taupe hover:text-charcoal px-3 py-2" data-testid="reactivate-cancel">
            never mind
          </button>
          <button type="submit" disabled={submitting} data-testid="reactivate-submit" className="bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase disabled:opacity-50">
            {submitting ? "reactivating…" : "reactivate piece"}
          </button>
        </div>
      </form>
    </div>
  );
}
